<template>
    <EditingDialog
        :context="context"
        :showSnackbar="true"
        snackbarTextLangConst="lang_GENERAL_SNACKBAR_TEXT_CLICK_MAIN_SAVE_BUTTON"
        :useDifferentShowDialogProp="true"
        :additionalAfterSaveFunction="refreshNoColumnMappingsWarning"
    >
        <template v-slot:editForm>
            <v-row>
                <v-col>
                    <BaseEditPageDropdown 
                        :context="context"
                        field="ColumnNumber"
                        :items="templateColumnItems"
                        :label="$t('lang_ACCOUNTS_MAPPINGS_CAPTION_TEMPLATE_COLUMN')"
                        rules="required"
                        :additionalFunction="setTemplateColumnString"
                        :withMargin="false"
                        :clearable="false"
                        :alvqa="alvqa + '-dropdown-template-column'"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col>
                    <BaseEditPageDropdown
                        :context="context"
                        field="EntityName"
                        :items="entityItems"
                        :label="$t('lang_ACCOUNT_MAPPINGS_CAPTION_ITEM')"
                        rules="required"
                        :withMargin="false"
                        :clearable="false"
                        :alvqa="alvqa + '-dropdown-entity-name'"
                    />
                </v-col>
                <v-col>
                    <BaseEditPageDropdown 
                        :context="context"
                        field="AlvField"
                        :items="sortedAlvFieldItems"
                        :label="$t('lang_ACCOUNTS_MAPPINGS_CAPTION_ALV_FIELD')"
                        :selection="true"
                        rules="required"
                        :withMargin="false"
                        :clearable="false"
                        :alvqa="alvqa + '-dropdown-alv-field'"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col>
                    <BaseCheckbox
                        :context="context + 'DetailsSkipDuplicates'"
                        :label="$t('lang_ACCOUNT_MAPPINGS_CAPTION_SKIP_DUPLICATES')"
                        :iconTooltipText="$t('lang_ACCOUNT_MAPPINGS_CAPTION_SKIP_DUPLICATES_DESCRIPTION')"
                        :alvqa="alvqa + '-skip-duplicates-checkbox'"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col>
                    <BaseEditPageTextarea 
                        :context="context" 
                        field="Formula"
                        rows="3"
                        :label="$t('lang_ACCOUNTS_MAPPINGS_CAPTION_FORMULA')"
                        :withMargin="false"
                        :rules="(isFormulaRequired === 'required' ? 'required|' : '') + 'validateMappingsFormula'"
                        :alvqa="alvqa + '-textarea-formula'"
                    />
                </v-col>
            </v-row>
        </template>
    </EditingDialog>
</template>

<script>
import EditingDialog from '@/components/_dialogs/EditingDialog'
import BaseEditPageAutocomplete from '@/components/_editForm/BaseEditPageAutocomplete'
import BaseEditPageDropdown from '@/components/_editForm/BaseEditPageDropdown'
import BaseEditPageTextarea from '@/components/_editForm/BaseEditPageTextarea'
import BaseCheckbox from '@/components/_base/BaseCheckbox'
import BaseTooltip from '@/components/_base/BaseTooltip'

import { DropdownAccountMappingsAlvFields, DropdownAccountMappingsDomainsOptions } from '@/assets/dropdowns/alvDropdowns.js'

export default {
    name: "ColumnMappingsEditPanelDialog",
    components: {
        EditingDialog,
        BaseEditPageAutocomplete,
        BaseEditPageDropdown,
        BaseEditPageTextarea,
        BaseCheckbox,
        BaseTooltip,
        DropdownAccountMappingsAlvFields,
    },
    data() {
        return {
            context: "ColumnMappings",
            alvqa: "account-mappings-edit-form-panel-column-mappings-edit-panel-dialog",
            selectedLanguage: this.$i18n.locale,
            sortedAlvFieldItems: [],
        }
    },
    computed: {
        templateColumnItems() { // read from uploaded template file
            let headers = this.$store.getters.getDropdownOfHeaders
            let headerCaptions = []
            let i = 0
            for (i in headers) {
                headerCaptions.push({text: headers[i], value: i});
            }
            return headerCaptions
        },
        alvFieldItems() {
            let allAlvFields    = DropdownAccountMappingsAlvFields
            let entityName          = this.selectedColumnEntity
            let direction       = this.$store.getters.getMappingsDetailsDirection
            if (entityName && direction) {
                return allAlvFields[direction + "_" + entityName]
            } else {
                return []
            }
        },
        isFormulaRequired() {
            let selectedColumn = this.$store.getters.getColumnMappingsDetailsTemplateColumn
            if (selectedColumn == "NOCOL") return "required"
            else return {}
        },
        selectedColumnEntity() {
            return this.$store.getters.getColumnMappingsDetailsEntityName;
        },
        isParentEntityDisabled() {
            let selectedEntity = this.$store.getters.getColumnMappingsDetailsEntityName
            let mappingDomain = this.$store.getters.getMappingsDetailsDomain
            
            if (selectedEntity == undefined || selectedEntity == null || mappingDomain == selectedEntity) {
                return true;
            }

            return false
        },
        entityItems() {
            let rootDomain = this.$store.getters.getMappingsDetailsDomain
            let option_map = {
                'publishing_works': [
                    {
                        value: 'publishing_works',
                        text: 'lang_PUBLISHING_CAPTION_WORKS'
                    },
                    {
                        value: 'publishing_works_interested_parties',
                        text: 'lang_PUBLISHING_WORKS_CAPTION_INTERESTED_PARTIES'
                    },
                    {
                        value: 'publishing_works_work_ids',
                        text: 'lang_GENERIC_CAPTION_OTHER_IDS'
                    },
                    {
                        value: 'publishing_works_instrumentations',
                        text: 'lang_PUBLISHING_WORKS_CAPTION_INSRUMENTATION'
                    },
                    {
                        value: 'publishing_works_other_titles',
                        text: 'lang_PUBLISHING_WORKS_CAPTION_OTHER_TITLES'
                    },
                    {
                        value: 'publishing_works_contracts',
                        text: 'lang_GENERIC_CAPTION_CONTRACTS'
                    }
                ],
                'publishing_sheets': [
                    {
                        value: 'publishing_sheets',
                        text: 'lang_PUBLISHING_CAPTION_SHEETS'
                    },
                    {
                        value: 'publishing_sheets_works',
                        text: 'lang_PUBLISHING_CAPTION_WORKS'
                    }
                ],
                'publishing_projects': [
                    {
                        value: 'publishing_projects',
                        text: 'lang_PUBLISHING_CAPTION_PROJECTS'
                    },
                    {
                        value: 'publishing_sheets',
                        text: 'lang_PUBLISHING_CAPTION_SHEETS'
                    },
                    {
                        value: 'publishing_sheets_works',
                        text: 'lang_PUBLISHING_CAPTION_WORKS'
                    }
                ],
                'label_releases': [
                    {
                        value: 'label_releases',
                        text: 'lang_LABEL_RELEASES'
                    },
                    {
                        value: 'label_tracks',
                        text: 'lang_LABEL_TRACKS'
                    },
                    {
                        value: 'label_releases_other_ids',
                        text: 'lang_GENERIC_CAPTION_OTHER_IDS'
                    },
                    {
                        value: 'label_tracks_other_ids',
                        text: 'lang_GENERIC_CAPTION_OTHER_IDS'
                    }
                ],
                'label_tracks': [
                    {
                        value: 'label_tracks',
                        text: 'lang_LABEL_TRACKS'
                    },
                    {
                        value: 'label_tracks_other_ids',
                        text: 'lang_GENERIC_CAPTION_OTHER_IDS'
                    }
                ],
                'az_persons': [
                    {
                        value: 'az_persons',
                        text: 'lang_GENERIC_CAPTION_DOMAIN_AZ_PERSONS'
                    },
                    {
                        value: 'az_persons_metadata',
                        text: 'lang_GENERIC_AZ_PANEL_CAPTION_METADATA'
                    },
                    {
                        value: 'az_persons_finance',
                        text: 'lang_AZ_PERSONS_PANEL_CAPTION_FINANCE'
                    },
                    {
                        value: 'az_persons_other_ids',
                        text: 'lang_AZ_PERSONS_CAPTION_PANEL_OTHER_IDS'
                    },
                ]
            }
            if (option_map[rootDomain]) {
                return option_map[rootDomain]
            } 

            let options = DropdownAccountMappingsDomainsOptions
            // find root domain from dropdown options and return it as single item array
            return [options.find((v) => v.direction === 'import' && rootDomain === v.value)]
        }
    },
    methods: {
        refreshNoColumnMappingsWarning() {
            // Keep the "no column mappings defined" alert in sync with the panel,
            // it must not stay on screen once a column mapping was added again.
            this.$store.dispatch('refreshNoColumnMappingsWarning')
        },
        setTemplateColumnString(item) {
            // // set templateColumn with Header string (nicer to read) in addition to columnNumber
            let headerString = this.$store.getters.getDropdownOfHeaders[item]
            this.$store.commit('setColumnMappingsDetailsTemplateColumn', headerString)
        },
        sortAlvFieldItems() {
            let items = JSON.parse(JSON.stringify(this.alvFieldItems)) // Make deep copy - avoid duplicating of alv field names in parenthesis (publishingIncomeWorkId) (publishingIncomeWorkId) ...
            this.sortedAlvFieldItems = []
            if (items.length) {
                items.sort((a, b) => {
                    let textA = this.$t(a.text)
                    let textB = this.$t(b.text)
                    if (textA < textB) return -1;
                    if (textA > textB) return 1;
                    return 0;
                })
            }

            for (let i = 0; i < items.length; i++) {
                items[i].text = this.$t(items[i].text) + ` (${items[i].value})`
            }

            this.sortedAlvFieldItems = items
        }
    },
    watch: {
        '$i18n.locale': {
            handler(newVal) {
                this.sortAlvFieldItems()
            },
            immediate: true
        },
        alvFieldItems: function(nevVal) {
            this.sortAlvFieldItems()
        }
    }
}

</script>